import { useState } from 'react';
import { useNavigate } from 'react-router';

import {
  ChevronUp,
  ChevronDown,
  Loader2,
  ArrowRight,
  Reply,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTemplates } from '@/hooks/useTemplatesAPI';
import PageLayout from '@/layouts/PageLayout';

const DocumentTempPage = () => {
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const itemsPerPage = 10;

  const { templates, loading, error, refetch } = useTemplates({
    sortBy,
    sortDirection,
    isDefault: true,
  });

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <ChevronDown className="h-4 w-4 opacity-50" />;
    }
    return sortDirection === 'asc' ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  const goToPage = (page) => {
    const totalPages = Math.ceil(templates.length / itemsPerPage);
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const renderListView = () => {
    const filteredTemplates = templates;
    const totalPages = Math.ceil(filteredTemplates.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedTemplates = filteredTemplates.slice(startIndex, endIndex);

    return (
      <PageLayout
        subtitle="Existing templates"
        title="Select Template"
        toolbarHead={
          <Button onClick={() => navigate('/documents')} variant="outline">
            <Reply className="h-4 w-4 mr-2" />
            Back
          </Button>
        }
      >
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-2">Loading templates...</span>
          </div>
        )}

        {error && !loading && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading templates
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
                <div className="mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={refetch}
                  >
                    Try again
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">
                    <Button
                      variant="ghost"
                      className="h-auto p-0 font-semibold hover:bg-transparent"
                      onClick={() => handleSort('type')}
                    >
                      Document Type
                      {getSortIcon('type')}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      className="h-auto p-0 font-semibold hover:bg-transparent"
                      onClick={() => handleSort('name')}
                    >
                      Template Name
                      {getSortIcon('name')}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      className="h-auto p-0 font-semibold hover:bg-transparent"
                      onClick={() => handleSort('creator')}
                    >
                      Creator
                      {getSortIcon('creator')}
                    </Button>
                  </TableHead>
                  <TableHead className="w-[120px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedTemplates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>
                      <Badge variant="outline">{template.type}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {template.name}
                    </TableCell>
                    <TableCell>{template.creator}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2 cursor-pointer">
                        <Button
                          variant="outline"
                          onClick={() =>
                            navigate(`/document/new/${template.id}`)
                          }
                        >
                          Select
                          <ArrowRight />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between space-x-2 py-4 px-4">
                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages} ({filteredTemplates.length}{' '}
                  templates)
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </PageLayout>
    );
  };

  return renderListView();
};

export default DocumentTempPage;
